import React from 'react';
import { AlertTriangle, Check } from 'lucide-react';
import StructuredQuestionForm from './StructuredQuestionForm';
import { PartEdit } from '../../../utils/partEdits';
import {
  ClarificationChatHistoryMessage,
  ClarificationChatReply,
  ClarificationChatTurn,
  ClarificationHint,
  ClarificationQuestion,
  StructuredAnswer,
} from './types';

// ── A question the customer skipped ──────────────────────────────────────────
// "Skip for now" postpones a clarification: the other lines load, and this one
// stays open in the result. The card says so and offers "Answer now". The
// question never unfolds inside the result: it opens where it is answered —
// the EPC diagram, or the AI chat sidebar (the table row's actions,
// `openSignal`). Both are overlays, so the form stays mounted but invisible.
// Only a list question with no drawing (front / rear …) opens in place, since
// its choices ARE the answer surface.

export type PostponedOpenMode = 'form' | 'diagram' | 'chat';

export interface SkippedQuestionCardProps {
  question: ClarificationQuestion;
  /** The customer's words and our reading: “bumper” → Bumper, Rear. */
  requestLine?: { original: string; label: string } | null;
  t: (key: string, opts?: any) => string;
  /** Already answered (on reload, or the line moved on). */
  answered?: boolean;
  disabled?: boolean;
  openSignal?: { mode: PostponedOpenMode; nonce: number } | null;
  onSubmit: (message: string, structuredAnswer: StructuredAnswer) => void;
  onRequestHint?: (question: ClarificationQuestion) => Promise<ClarificationHint>;
  onChatAboutQuestion?: (question: ClarificationQuestion, messages: ClarificationChatTurn[]) => Promise<ClarificationChatReply>;
  onLoadChatHistory?: (question: ClarificationQuestion) => Promise<ClarificationChatHistoryMessage[]>;
  chatContext?: string | null;
  /** The side chat's "search X instead" / "also search X" (see StructuredQuestionForm). */
  onPartEdit?: (edits: PartEdit[]) => void;
}

const AMBER = '#9a6700';

export const skippedCardId = (questionId: string) => `skipped-question-${questionId}`;

const SkippedQuestionCard: React.FC<SkippedQuestionCardProps> = ({
  question, requestLine, t, answered = false, disabled = false, openSignal,
  onSubmit, onRequestHint, onChatAboutQuestion, onLoadChatHistory, chatContext, onPartEdit,
}) => {
  // 'inline' shows the question under the card; 'overlay' keeps it mounted
  // (the diagram / chat it opened are portals) but out of the result.
  const [open, setOpen] = React.useState<false | 'inline' | 'overlay'>(false);
  const hasDiagram = (question.options || []).some(o => (o.load || []).some(r => !!r.group_id && !!r.section_id));
  const [sent, setSent] = React.useState(false);
  const [autoOpen, setAutoOpen] = React.useState<{ mode: 'diagram' | 'chat'; nonce: number } | null>(null);
  const ref = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!openSignal || answered || sent) return;
    if (openSignal.mode === 'form' && !hasDiagram) {
      setOpen('inline');
      setAutoOpen(null);
    } else {
      setOpen('overlay');
      setAutoOpen({ mode: openSignal.mode === 'chat' ? 'chat' : 'diagram', nonce: openSignal.nonce });
    }
    ref.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, [openSignal?.nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  const done = answered || sent;
  const original = (requestLine?.original || '').trim();
  const label = (requestLine?.label || question.part_label || question.part_name || '').trim();
  const pair = original && original.toLowerCase() !== label.toLowerCase()
    ? <>“{original}” <span aria-hidden="true">→</span> {label}</>
    : <>{label}</>;

  return (
    <div ref={ref} id={skippedCardId(question.id)} data-testid="skipped-question" style={{ marginTop: 10 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        padding: '12px 16px', background: '#ffffff', border: '1px solid #e7e5f0', borderRadius: 16,
        boxShadow: '0 1px 2px rgba(20,20,40,0.04)',
      }}>
        <span style={{
          flexShrink: 0, width: 30, height: 30, borderRadius: '50%', display: 'inline-flex',
          alignItems: 'center', justifyContent: 'center',
          background: done ? '#d8f3ea' : '#fbecc4', color: done ? '#137F67' : AMBER,
        }}>
          {done ? <Check size={15} /> : <AlertTriangle size={15} />}
        </span>
        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#201e1a' }}>
            {done
              ? t('chat.clarification.skippedAnswered', { defaultValue: 'Skipped question — answer sent' })
              : t('chat.clarification.skippedTitle', { defaultValue: 'Question skipped — no part selected yet' })}
          </div>
          <div style={{ fontSize: 13, color: '#6c6978', marginTop: 2, overflowWrap: 'anywhere' }}>
            {pair}
            {!done && <> · {t('chat.clarification.skippedSub', { defaultValue: 'the result stays open until you pick the part or add context' })}</>}
          </div>
        </div>
        {!done && open !== 'inline' && (
          <button
            type="button"
            onClick={() => {
              if (hasDiagram) { setOpen('overlay'); setAutoOpen({ mode: 'diagram', nonce: Date.now() }); }
              else { setOpen('inline'); setAutoOpen(null); }
            }}
            disabled={disabled}
            style={{
              flexShrink: 0, padding: '7px 16px', borderRadius: 999, border: '1px solid #d9d7e3',
              background: '#ffffff', color: '#201e1a', fontSize: 14, fontWeight: 600,
              cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit', opacity: disabled ? 0.6 : 1,
            }}
          >
            {t('chat.clarification.answerNow', { defaultValue: 'Answer now' })}
          </button>
        )}
      </div>
      {open && !done && (
        <div data-testid="skipped-question-form" style={open === 'overlay' ? { height: 0, overflow: 'hidden' } : undefined}
          aria-hidden={open === 'overlay' ? true : undefined}>
        <StructuredQuestionForm
          questions={[question]}
          t={t}
          requestLines={requestLine ? { [question.id]: requestLine } : undefined}
          onRequestHint={onRequestHint}
          onChatAboutQuestion={onChatAboutQuestion}
          onLoadChatHistory={onLoadChatHistory}
          chatContext={chatContext}
          onPartEdit={onPartEdit}
          disabled={disabled}
          hideSkip
          autoOpen={autoOpen}
          onSubmit={(message, answer) => { setSent(true); onSubmit(message, answer); }}
        />
        </div>
      )}
    </div>
  );
};

export default SkippedQuestionCard;
